import {TerminalData} from '../../../api/payments/types'

/*
 * Can this venue take money online?
 *
 * The client-side answer to the question the server answers in
 * `functions/controllers/bookings/helpers/online-payability.js` (`isOnlinePayable`).
 * `GET /payments/terminalName` returns the terminal that would ACTUALLY take the money — the
 * non-tokenized, one-time one — and 404s when the venue has none. No terminal means every online
 * purchase path ends in a dead end, so the UI has to say so up front instead of walking the
 * customer into it and failing at the pay step.
 *
 * A group venue (Organizations v1) is the case that has this today: it sells one membership valid
 * across its child venues and deliberately has no terminal of its own, because money never splits.
 * Nothing below is group-specific — any venue without a usable terminal reads the same.
 */

export enum OnlinePayability {
  /** A terminal the pay flow can use exists. */
  AVAILABLE = 'AVAILABLE',
  /** The venue has no usable terminal: online purchase is impossible, not merely slow. */
  NO_TERMINAL = 'NO_TERMINAL',
  /** Not decided: the lookup has not resolved, or it failed for a reason other than "no terminal". */
  UNKNOWN = 'UNKNOWN',
}

export const TERMINAL_NOT_FOUND_STATUS = 404

/**
 * A 404 from the terminal lookup is the venue answering "I have no terminal". Every other failure
 * (network drop, 500, auth) is a transient unknown and must NOT be reported as "cannot pay" — that
 * would disable a working venue's purchase button on a blip.
 */
export const isTerminalNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false
  }
  const {response} = error as {response?: {status?: number}}
  return response?.status === TERMINAL_NOT_FOUND_STATUS
}

export type TerminalLookupStatus = 'error' | 'idle' | 'loading' | 'success'

export type ResolveOnlinePayabilityArgs = {
  venueId?: string | null
  status: TerminalLookupStatus
  terminal?: TerminalData | null
  error?: unknown
}

export const resolveOnlinePayability = ({
  venueId,
  status,
  terminal,
  error,
}: ResolveOnlinePayabilityArgs): OnlinePayability => {
  // Nothing has been asked yet — never claim a venue cannot be paid before its id is known.
  if (!venueId) {
    return OnlinePayability.UNKNOWN
  }
  if (status === 'idle' || status === 'loading') {
    return OnlinePayability.UNKNOWN
  }
  if (status === 'error') {
    return isTerminalNotFoundError(error) ? OnlinePayability.NO_TERMINAL : OnlinePayability.UNKNOWN
  }
  return terminal ? OnlinePayability.AVAILABLE : OnlinePayability.NO_TERMINAL
}
